import React, { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnResizeMode,
  RowSelectionState,
  VisibilityState,
  OnChangeFn,
  ColumnSizingState
} from '@tanstack/react-table';
import { ContextMenu } from '@base-ui/react/context-menu';
import { Menu, MenuItem } from './Menu';
import {
  CaretLeftIcon,
  CaretRightIcon,
  DotsThreeVerticalIcon,
  WarningIcon,
  CaretUpIcon,
  CaretDownIcon,
  CaretUpDownIcon,
  ArrowsOutLineHorizontalIcon,
  Icon
} from '@phosphor-icons/react';
import type { MenuItemIconProps } from './Menu';

const stickyHeaderCellClass =
  'sticky top-0 z-20 bg-surface before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-border-subtle after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-border-subtle';

interface Action<T> {
  label: React.ReactNode | ((item: T) => React.ReactNode);
  icon: Icon;
  iconProps?: MenuItemIconProps | ((item: T) => MenuItemIconProps);
  onClick: (item: T) => void;
  className?: string;
  disabled?: (item: T) => boolean;
  disabledTitle?: string | ((item: T) => string);
  /** When this returns true the action is omitted from the menu entirely. */
  hidden?: (item: T) => boolean;
}

interface TanStackTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  onRowClick?: (item: T) => void;
  actions?: Action<T>[];
  /** Open the row action menu on right-click when that row has visible actions. */
  contextMenu?: boolean;
  itemsPerPage?: number;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  multiselect?: boolean;
  /** When `multiselect` is true, whether more than one row can be selected. Defaults to true. */
  enableMultiRowSelection?: boolean;
  /** When multiselect is on, only these rows can be checked. Others still receive onRowClick. */
  isRowSelectable?: (item: T) => boolean;
  clearSelection?: boolean;
  error?: string | null;
  emptyMessage?: React.ReactNode;
  rowTitle?: (item: T) => string;
  enableSorting?: boolean;
  /** When true, header clicks update sort state but rows are not reordered locally. */
  manualSorting?: boolean;
  enableResizing?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Shift-click multi-sort. Defaults to TanStack’s true. */
  enableMultiSort?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
  getRowId?: (row: T) => string;
  onHoveredRowChange?: (rowId: string | number | null) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  /** Total number of pages (for server-side pagination) */
  pageCount?: number;
  /** Total number of items across all pages (for server-side pagination) */
  totalItems?: number;
  /** Whether to hide the column headers */
  hideHeaders?: boolean;
  /** Whether rows should auto-expand based on cell content instead of fixed height */
  autoRowHeight?: boolean;
  /**
   * ID of the column that should behave as a "fluid" column - filling all remaining space.
   *
   * ## How Fluid Columns Work:
   *
   * 1. **Initial State**: When a column is marked as fluid and hasn't been manually resized by the user,
   *    its width style is set to `undefined`, allowing it to naturally expand to fill remaining table space.
   *    Other columns use their defined `size` or `minSize` values.
   *
   * 2. **User Resizing**: When a user manually resizes ANY column (including the fluid column), the resize
   *    is stored in `columnSizing` state. The fluid column then respects this manual width like any other column.
   *
   * 3. **Window Resizing**: When the window/viewport is resized, the parent component should reset `columnSizing`
   *    to an empty object `{}`. This clears all manual resize state and returns the fluid column to its
   *    flexible behavior, allowing it to adapt to the new viewport width.
   *
   * 4. **Responsive Columns**: This approach works harmoniously with responsive column visibility. As columns
   *    are hidden/shown based on viewport width, the fluid column automatically adjusts to fill the space.
   *
   * ## Benefits:
   * - **No horizontal scrolling**: The fluid column absorbs extra space, preventing unwanted scrollbars
   * - **User control**: Users can still manually resize any column including the fluid one
   * - **Responsive**: Automatically adapts when viewport changes or columns are hidden/shown
   * - **Simple**: Uses CSS table behavior rather than complex JavaScript calculations
   *
   * @example
   * ```tsx
   * // Reset column sizing on window resize to restore fluid behavior
   * useEffect(() => {
   *   setColumnSizing({}); // Clear manual resizes
   * }, [windowWidth]);
   *
   * <TanStackTable
   *   columns={columns}
   *   fluidColumnId="title"  // Title column fills remaining space
   *   columnSizing={columnSizing}
   *   onColumnSizingChange={setColumnSizing}
   * />
   * ```
   */
  fluidColumnId?: string;
  /** Minimum height for the table container to prevent layout shifts when rows change */
  minHeight?: string | number;
  /** Vertical alignment for table body cells */
  cellVerticalAlign?: 'top' | 'middle';
  /** Header height density */
  headerSize?: 'compact' | 'comfortable';
  /** Extra props to spread onto each <tr> element */
  getRowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * Give the table a minimum width equal to the sum of its visible columns' resolved widths
   * (`getSize()`), so a narrow viewport scrolls the container horizontally instead of compressing
   * columns. Off by default: `table-layout: fixed` with `width: 100%` otherwise squeezes columns
   * past their floors and the `overflow-x-auto` container never scrolls. A fluid column
   * (`fluidColumnId`) carries no explicit `size`, so it contributes its `minSize` to the floor —
   * `getSize()` would return the default width and undershoot the width it should keep.
   */
  scrollFloorFromColumns?: boolean;
  /** Keep the header visible while the surrounding list scrolls. */
  stickyHeader?: boolean;
}

// Extracted Pagination component
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const getPaginationRange = (currentPage: number, totalPages: number): (number | string)[] => {
    const delta = 0;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      } else if (i === currentPage - delta - 1 || i === currentPage + delta + 1) {
        range.push('...');
      }
    }

    return range;
  };

  return (
    <div className="flex pl-1 py-2 text-sm items-center justify-between border-b-2 border-border-subtle">
      <div className="flex items-center">
        <p className="text-sm text-content-default">
          {startIndex + 1}-{endIndex} of {totalItems} items
        </p>
      </div>

      <div className="flex items-center gap-1 text-content-subtle text-xs">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="hover:bg-surface-hover w-[26px] h-[26px] flex items-center justify-center rounded-md p-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CaretLeftIcon size={16} />
        </button>
        <div className="flex items-center space-x-1">
          {getPaginationRange(currentPage, totalPages).map((page, index) =>
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-1 text-content-subtle">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page as number)}
                className={`w-[26px] h-[26px] flex items-center justify-center rounded-md text-xs font-medium ${
                  currentPage === page
                    ? 'bg-secondary-blue text-primary-lightblue'
                    : 'text-content-subtle hover:bg-surface-hover'
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="hover:bg-surface-hover w-[26px] h-[26px] flex items-center justify-center rounded-md p-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CaretRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/** Same actions for the row menu button and the right-click menu. */
function SharedMenuItems<T>({
  contextMenu = false,
  item,
  actions
}: {
  contextMenu?: boolean;
  item: T;
  actions: Action<T>[];
}) {
  return (
    <>
      {actions.map((action, index) => {
        if (action.hidden?.(item)) return null;
        const isDisabled = action.disabled?.(item) ?? false;
        const disabledTitle = isDisabled
          ? typeof action.disabledTitle === 'function'
            ? action.disabledTitle(item)
            : action.disabledTitle
          : undefined;
        return (
          <MenuItem
            key={index}
            contextMenu={contextMenu}
            disabled={isDisabled}
            title={disabledTitle || undefined}
            icon={action.icon}
            iconProps={typeof action.iconProps === 'function' ? action.iconProps(item) : action.iconProps}
            className={action.className}
            onClick={() => {
              if (isDisabled) return;
              action.onClick(item);
            }}
          >
            {typeof action.label === 'function' ? action.label(item) : action.label}
          </MenuItem>
        );
      })}
    </>
  );
}

/**
 * TanStackTable - A feature-rich table component built on TanStack Table v8
 *
 * ## Column Auto-Resizing Strategy
 *
 * This table implements a flexible column sizing approach that balances automatic layout
 * with user control:
 *
 * ### Fluid Column Concept
 * One column (typically the main content column like "Title") can be designated as "fluid"
 * via the `fluidColumnId` prop. This column automatically fills remaining space, preventing
 * horizontal scrolling while giving maximum space to content.
 *
 * ### Three States of Column Sizing
 *
 * 1. **Initial/Auto State**:
 *    - Fixed columns use their defined `size` or `minSize`
 *    - Fluid column has no explicit width, fills remaining space
 *    - Result: Table fits container width without scrolling
 *
 * 2. **User-Resized State**:
 *    - User drags column resize handles
 *    - Sizes stored in `columnSizing` state
 *    - All columns (including fluid) respect manual widths
 *    - Result: User has full control over column widths
 *
 * 3. **Reset State** (on window resize):
 *    - Parent clears `columnSizing` to `{}`
 *    - Returns to Initial/Auto state
 *    - Fluid column adapts to new viewport width
 *    - Result: Table reoptimizes for new screen size
 *
 * ### Responsive Column Visibility
 * Works seamlessly with columns being hidden/shown at different breakpoints.
 * The fluid column automatically adjusts to fill space as columns appear/disappear.
 *
 * @example
 * ```tsx
 * // In parent component (e.g., DocumentBrowser):
 * const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
 *
 * // Reset on window resize to restore fluid behavior
 * useEffect(() => {
 *   setColumnSizing({}); // Clears manual resizes
 * }, [windowWidth]);
 *
 * <TanStackTable
 *   columns={columns}
 *   fluidColumnId="title"
 *   columnSizing={columnSizing}
 *   onColumnSizingChange={setColumnSizing}
 * />
 * ```
 */
export default function TanStackTable<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  actions,
  contextMenu = false,
  itemsPerPage = 10,
  onSelectionChange,
  multiselect = false,
  enableMultiRowSelection = true,
  isRowSelectable,
  clearSelection = false,
  error,
  emptyMessage,
  rowTitle,
  enableSorting = true,
  manualSorting = false,
  enableResizing = true,
  columnVisibility: externalColumnVisibility,
  onColumnVisibilityChange: externalOnColumnVisibilityChange,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
  enableMultiSort = true,
  rowSelection: externalRowSelection,
  onRowSelectionChange: externalOnRowSelectionChange,
  columnSizing: externalColumnSizing,
  onColumnSizingChange: externalOnColumnSizingChange,
  getRowId,
  onHoveredRowChange,
  currentPage: externalCurrentPage,
  onPageChange: externalOnPageChange,
  pageCount,
  totalItems: externalTotalItems,
  fluidColumnId,
  hideHeaders = false,
  autoRowHeight = false,
  minHeight,
  cellVerticalAlign = 'middle',
  headerSize = 'compact',
  getRowProps,
  scrollFloorFromColumns = false,
  stickyHeader = false
}: TanStackTableProps<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>({});
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [actionMenu, setActionMenu] = useState<{ rowId: string; anchor: HTMLElement } | null>(null);
  const [, setHoveredRow] = useState<string | number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [showResizeHint, setShowResizeHint] = useState(() => {
    // Check localStorage to see if user has already resized columns
    const hasResized = localStorage.getItem('tanstack-table-resize-hint-dismissed');
    return !hasResized;
  });
  const tableRef = useRef<HTMLTableElement>(null);

  // Use external state if provided, otherwise use internal
  const sorting = externalSorting !== undefined ? externalSorting : internalSorting;
  const onSortingChange = externalOnSortingChange || setInternalSorting;
  const columnVisibility = externalColumnVisibility !== undefined ? externalColumnVisibility : internalColumnVisibility;
  const onColumnVisibilityChangeHandler = externalOnColumnVisibilityChange || setInternalColumnVisibility;
  const rowSelection = externalRowSelection !== undefined ? externalRowSelection : internalRowSelection;
  const onRowSelectionChangeHandler = externalOnRowSelectionChange || setInternalRowSelection;
  const columnSizing = externalColumnSizing !== undefined ? externalColumnSizing : internalColumnSizing;
  const onColumnSizingChangeHandler = externalOnColumnSizingChange || setInternalColumnSizing;
  const currentPageState = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
  const onPageChangeHandler = externalOnPageChange || setInternalCurrentPage;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange' as ColumnResizeMode,
    enableSorting,
    enableMultiSort,
    manualSorting,
    enableColumnResizing: enableResizing,
    enableRowSelection: multiselect && isRowSelectable ? (row) => isRowSelectable(row.original) : multiselect,
    enableMultiRowSelection: Boolean(multiselect && enableMultiRowSelection),
    getRowId: getRowId || ((row, index) => row.id?.toString() || index.toString()),
    // Enable manual pagination when pageCount is provided (server-side pagination)
    manualPagination: pageCount !== undefined,
    pageCount: pageCount,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnSizing,
      pagination: {
        pageSize: itemsPerPage,
        pageIndex: currentPageState - 1
      }
    },
    onSortingChange,
    onColumnVisibilityChange: onColumnVisibilityChangeHandler,
    onRowSelectionChange: onRowSelectionChangeHandler,
    onColumnSizingChange: onColumnSizingChangeHandler,
    initialState: {
      pagination: {
        pageSize: itemsPerPage,
        pageIndex: currentPageState - 1
      }
    }
  });

  // Handle clearSelection prop
  useEffect(() => {
    if (clearSelection) {
      table.resetRowSelection();
    }
  }, [clearSelection, table]);

  // Notify parent of selection changes
  const lastSelectedIdsRef = useRef<(string | number)[]>([]);
  useEffect(() => {
    if (onSelectionChange) {
      const selectedIds = Object.entries(rowSelection)
        .filter(([_, selected]) => selected)
        .map(([id]) => {
          // Try to convert to number if possible
          const numId = Number(id);
          return isNaN(numId) ? id : numId;
        });

      // Break potential loops by checking if the IDs have actually changed
      const currentIds = selectedIds.map(String).sort();
      const lastIds = lastSelectedIdsRef.current.map(String).sort();

      if (JSON.stringify(currentIds) !== JSON.stringify(lastIds)) {
        lastSelectedIdsRef.current = selectedIds;
        onSelectionChange(selectedIds);
      }
    }
  }, [rowSelection, onSelectionChange, table]);

  const currentPage = currentPageState;
  const totalPages = pageCount !== undefined ? pageCount : table.getPageCount();

  // Reset to page 1 if current page is beyond total pages (e.g., after filtering)
  useEffect(() => {
    if (pageCount === undefined) return;
    if (pageCount > 0 && currentPageState > pageCount) {
      onPageChangeHandler(1);
    }
  }, [pageCount, currentPageState, onPageChangeHandler]);

  // Floor the table at the sum of its columns' resolved widths so a narrow container scrolls rather
  // than compressing the fluid column below its minSize; the inline min-width overrides `min-w-full`.
  // getSize() ignores minSize, so the fluid column contributes its minSize to keep the floor honest.
  const scrollFloor = scrollFloorFromColumns
    ? table.getVisibleLeafColumns().reduce((sum, column) => {
        const width = column.id === fluidColumnId ? (column.columnDef.minSize ?? column.getSize()) : column.getSize();
        return sum + width;
      }, 0)
    : undefined;

  return (
    <div className="space-y-4">
      <div
        className={stickyHeader ? 'w-full overflow-visible' : 'w-full overflow-x-auto'}
        style={{
          minHeight
        }}
      >
        <table ref={tableRef} className="w-full min-w-full" style={{ tableLayout: 'fixed', minWidth: scrollFloor }}>
          {!hideHeaders && (
            <thead>
              <tr className={stickyHeader ? undefined : 'border-t-2 border-b-2 border-border-subtle'}>
                {table.getHeaderGroups().map((headerGroup) => {
                  // Find the first resizable column for the hint
                  const firstResizableHeader = headerGroup.headers.find((h) => h.column.getCanResize());
                  // Count visible columns to hide resize handle when only one column
                  const visibleColumnCount = headerGroup.headers.length;
                  const isSingleColumn = visibleColumnCount === 1;

                  return headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const isSorted = header.column.getIsSorted();
                    const canResize = header.column.getCanResize();
                    const isFirstResizable = firstResizableHeader?.id === header.id;

                    // Fluid column behavior: If this is the designated fluid column and the user
                    // hasn't manually resized it, we set width to undefined. This allows the browser's
                    // table layout algorithm to make this column fill all remaining space after
                    // other columns take their defined widths. Once the user resizes any column,
                    // that resize is stored in columnSizing state, and we respect that manual width.
                    // Also, when there's only one column, always make it fill the full width.
                    const isFluid = fluidColumnId === header.column.id;
                    const hasUserResized = columnSizing && columnSizing[header.column.id] !== undefined;
                    const widthStyle = isSingleColumn || (isFluid && !hasUserResized) ? undefined : header.getSize();

                    return (
                      <th
                        key={header.id}
                        className={`px-4 ${headerSize === 'comfortable' ? 'py-2' : 'py-0.5'} text-left text-sm font-medium min-w-0 ${
                          stickyHeader ? stickyHeaderCellClass : 'relative'
                        } ${canSort ? 'cursor-pointer select-none' : ''}`}
                        style={{
                          width: widthStyle,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize
                        }}
                        onClick={(e) => {
                          // Don't sort if we just finished resizing or clicked on resize handle
                          if (canSort && !isResizing && !(e.target as HTMLElement).closest('.resize-handle')) {
                            header.column.getToggleSortingHandler()?.(e);
                          }
                        }}
                      >
                        <div className="min-w-0 truncate flex items-center gap-2 text-content-subtle">
                          {header.isPlaceholder ? null : (
                            <>
                              <span className="truncate">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {canSort &&
                                (isSorted ? (
                                  isSorted === 'asc' ? (
                                    <CaretUpIcon size={14} />
                                  ) : (
                                    <CaretDownIcon size={14} />
                                  )
                                ) : (
                                  <CaretUpDownIcon size={14} className="text-content-tertiary" />
                                ))}
                            </>
                          )}
                        </div>
                        {/* Hide resize handle when there's only one column - nothing to resize relative to */}
                        {canResize && !isSingleColumn && (
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsResizing(true);

                              // Dismiss hint and save to localStorage
                              if (showResizeHint) {
                                setShowResizeHint(false);
                                localStorage.setItem('tanstack-table-resize-hint-dismissed', 'true');
                              }

                              const resizeHandler = header.getResizeHandler();
                              if (resizeHandler) {
                                resizeHandler(e);
                              }

                              // Clear resizing flag after a delay
                              const handleMouseUp = () => {
                                setTimeout(() => setIsResizing(false), 100);
                                document.removeEventListener('mouseup', handleMouseUp);
                              };
                              document.addEventListener('mouseup', handleMouseUp);
                            }}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsResizing(true);

                              // Dismiss hint and save to localStorage
                              if (showResizeHint) {
                                setShowResizeHint(false);
                                localStorage.setItem('tanstack-table-resize-hint-dismissed', 'true');
                              }

                              const resizeHandler = header.getResizeHandler();
                              if (resizeHandler) {
                                resizeHandler(e);
                              }

                              // Clear resizing flag after a delay
                              const handleTouchEnd = () => {
                                setTimeout(() => setIsResizing(false), 100);
                                document.removeEventListener('touchend', handleTouchEnd);
                              };
                              document.addEventListener('touchend', handleTouchEnd);
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="resize-handle absolute right-0 top-1/2 -translate-y-1/2 h-4 w-3 cursor-col-resize select-none touch-none flex items-center justify-center group"
                          >
                            {/* Visual resize icon hint - only on first resizable column */}
                            {showResizeHint && isFirstResizable && (
                              <ArrowsOutLineHorizontalIcon
                                size={12}
                                weight="bold"
                                className="absolute text-content-subtle opacity-0 group-hover:opacity-100 transition-opacity duration-200 animate-pulse pointer-events-none"
                              />
                            )}
                            {/* Traditional resize line */}
                            <div
                              className={`h-full transition-all ${
                                header.column.getIsResizing()
                                  ? 'bg-blue-400 w-0.5'
                                  : 'bg-border-default group-hover:bg-content-subtlest group-active:bg-content-subtlest w-px'
                              }`}
                            />
                          </div>
                        )}
                      </th>
                    );
                  });
                })}
                {actions && actions.length > 0 && (
                  <th className={`w-[48px] h-[48px] ${stickyHeader ? stickyHeaderCellClass : ''}`} />
                )}
              </tr>
            </thead>
          )}
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0)}
                  className="text-center text-content-subtle py-6"
                >
                  {error ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-misc-error">
                      <WarningIcon size={24} />
                      <span className="text-base font-medium">{error}</span>
                    </div>
                  ) : emptyMessage ? (
                    <div className="flex flex-col items-center justify-center gap-2">{emptyMessage}</div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="text-base font-medium">No data available</span>
                      <span className="text-sm text-content-tertiary">
                        Try adjusting your filters or adding new items.
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const { className: extraClassName, ...extraRowProps } = getRowProps?.(row.original) ?? {};
                const visibleContextActions =
                  contextMenu && actions ? actions.filter((action) => !action.hidden?.(row.original)) : [];
                const rowElement = (
                  <tr
                    className={twMerge(
                      'data-table-row text-sm transition-colors border-b border-border-subtle',
                      onRowClick || multiselect ? 'cursor-pointer hover:bg-bluegray-100 active:bg-bluegray-200' : '',
                      row.getIsSelected() ? 'bg-blue-50' : '',
                      extraClassName
                    )}
                    {...extraRowProps}
                    tabIndex={onRowClick ? 0 : extraRowProps.tabIndex}
                    onClick={() => {
                      if (multiselect && row.getCanSelect()) row.toggleSelected();
                      onRowClick?.(row.original);
                    }}
                    onKeyDown={(event) => {
                      extraRowProps.onKeyDown?.(event);
                      if (!onRowClick) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      if ((event.target as HTMLElement | null)?.closest('button, a, input, textarea, select')) return;
                      event.preventDefault();
                      if (multiselect && row.getCanSelect()) row.toggleSelected();
                      onRowClick(row.original);
                    }}
                    onMouseEnter={() => {
                      setHoveredRow(row.id);
                      onHoveredRowChange?.(row.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredRow(null);
                      onHoveredRowChange?.(null);
                    }}
                    title={rowTitle ? rowTitle(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell, _, cells) => {
                      // Apply the same fluid column logic to table cells as we do to headers
                      // Also, when there's only one column, always make it fill the full width
                      const isSingleColumn = cells.length === 1;
                      const isFluid = fluidColumnId === cell.column.id;
                      const hasUserResized = columnSizing && columnSizing[cell.column.id] !== undefined;
                      const widthStyle =
                        isSingleColumn || (isFluid && !hasUserResized) ? undefined : cell.column.getSize();

                      return (
                        <td
                          key={cell.id}
                          className={`px-4 text-left text-sm font-normal text-content-default min-w-0 ${
                            cellVerticalAlign === 'top' ? 'align-top' : 'align-middle'
                          } ${autoRowHeight ? 'py-2' : 'h-[48px]'}`}
                          style={{
                            width: widthStyle,
                            minWidth: cell.column.columnDef.minSize,
                            maxWidth: cell.column.columnDef.maxSize
                          }}
                        >
                          <div className={autoRowHeight ? 'min-w-0' : 'min-w-0 truncate'}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      );
                    })}
                    {actions &&
                      actions.length > 0 &&
                      (() => {
                        const visibleActions = actions.filter((a) => !a.hidden?.(row.original));
                        return visibleActions.length === 0 ? (
                          <td className="w-[48px] h-[48px]" />
                        ) : (
                          <td
                            className={`w-[48px] h-[48px] relative ${cellVerticalAlign === 'top' ? 'align-top' : 'align-middle'}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              aria-label={rowTitle ? `Actions for ${rowTitle(row.original)}` : 'Row actions'}
                              className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-content-default hover:bg-bluegray-100"
                              onClick={(event) => {
                                event.stopPropagation();
                                const anchor = event.currentTarget;
                                setActionMenu((current) =>
                                  current?.rowId === row.id ? null : { rowId: row.id, anchor }
                                );
                              }}
                            >
                              <DotsThreeVerticalIcon size={18} />
                            </button>
                          </td>
                        );
                      })()}
                  </tr>
                );
                if (visibleContextActions.length === 0) {
                  return <React.Fragment key={row.id}>{rowElement}</React.Fragment>;
                }
                return (
                  <ContextMenu.Root key={row.id}>
                    <ContextMenu.Trigger render={rowElement} />
                    <ContextMenu.Portal>
                      <ContextMenu.Positioner className="z-[9999] outline-none">
                        <ContextMenu.Popup className="rounded-md bg-white py-1 shadow-md ring-1 ring-border-default outline-none">
                          <SharedMenuItems contextMenu item={row.original} actions={actions ?? []} />
                        </ContextMenu.Popup>
                      </ContextMenu.Positioner>
                    </ContextMenu.Portal>
                  </ContextMenu.Root>
                );
              })
            )}
          </tbody>
          {table.getFooterGroups().some((group) => group.headers.some((header) => header.column.columnDef.footer)) && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id} className="border-t-2 border-border-subtle bg-surface-subtle">
                  {footerGroup.headers.map((header) => {
                    const isSingleColumn = footerGroup.headers.length === 1;
                    const isFluid = fluidColumnId === header.column.id;
                    const hasUserResized = columnSizing && columnSizing[header.column.id] !== undefined;
                    const widthStyle = isSingleColumn || (isFluid && !hasUserResized) ? undefined : header.getSize();
                    return (
                      <td
                        key={header.id}
                        className="px-4 h-[48px] text-left text-sm font-medium text-content-default min-w-0 align-middle"
                        style={{
                          width: widthStyle,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={externalTotalItems ?? data.length}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChangeHandler}
        />
      )}
      {actions &&
        actionMenu &&
        (() => {
          const actionRow = table.getRowModel().rows.find((row) => row.id === actionMenu.rowId);
          if (!actionRow) return null;
          return (
            <Menu
              anchorEl={actionMenu.anchor}
              open
              onClose={() => setActionMenu(null)}
              placement="bottom-end"
              positionerClassName="z-[9999]"
            >
              <SharedMenuItems item={actionRow.original} actions={actions} />
            </Menu>
          );
        })()}
    </div>
  );
}
